import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext, loadVisibleTenants } from '../shared.jsx';
import ConflictProcess from './ConflictProcess.jsx';
import { getConflictAction } from './ConflictUtils.jsx';
import ConflictHistory from './ConflictHistory.jsx';
import { InfoCard, MentorMessagingPanel } from './MentorComponents.jsx';
import { AnimalAvatar } from './AnimalAvatars.jsx';
import ReviewProposedChangesView from './ReviewProposedChangesView.jsx';
import TermsAcceptanceView from './TermsAcceptanceView.jsx';
import { ConflictExportButton } from './ConflictExport.jsx';
import { StoryModeButton } from './StoryMode.jsx';
import ModalDialog from './ModalDialog.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, emailService, detectHarmContent, detectUnconstructiveContent, containsProfanity, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase, getTenantUsersKey, loadAllRelationships, computeRelationalAccessSnapshot, directoryClient } from '../shared.jsx';
import { useBilling } from '../modules/billing/BillingProvider.jsx';

function ConflictDetailView({ conflict, user, onBack, onUpdate, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [forceProcessStep, setForceProcessStep] = useState(null);
  const [deactivatedUserIds, setDeactivatedUserIds] = useState(new Set());

  // P3: Load deactivated user IDs for visual indicators
  useEffect(() => {
    const loadDeactivated = async () => {
      try {
        const allTenants = await loadVisibleTenants();
        const deactivated = new Set();
        for (const tenant of (Array.isArray(allTenants) ? allTenants : [])) {
          const tenantUsers = await storage.get(getTenantUsersKey(tenant.id), true) || [];
          for (const u of (Array.isArray(tenantUsers) ? tenantUsers : [])) {
            if (u.isActive === false && u.id) deactivated.add(u.id);
          }
        }
        setDeactivatedUserIds(deactivated);
      } catch (e) { /* best effort */ }
    };
    loadDeactivated();
  }, [conflict.id]);

  const isResolved = conflict.status === 'resolved';
  const isObserverRole = ['fly-on-wall', 'fly-on-the-wall', 'omniscient'].includes(user.role);
  const canEdit = !isResolved && !isObserverRole && (
    conflict.mentees?.some(m => m.id === user.id || m.email.toLowerCase() === user.email.toLowerCase()) ||
                  user.role === 'mentor' ||
                  user.role === 'admin'
  );

  // Check if user is actually a mentee (not mentor, fly-on-wall, omniscient, or admin viewing)
  const isActiveMentee = conflict.mentees?.some(m => m.id === user.id || m.email?.toLowerCase() === user.email?.toLowerCase());

  // Check if user needs to accept terms — only mentees who aren't the creator
  const needsToAcceptTerms = isActiveMentee &&
                             conflict.termsAcceptance?.required && 
                             conflict.termsAcceptance?.status === 'pending' &&
                             !conflict.termsAcceptance?.acceptedBy?.includes(user.id) &&
                             conflict.createdBy !== user.id;

  // Check if user is creator and there are proposed changes to review
  const needsToReviewChanges = conflict.termsAcceptance?.status === 'changes-proposed' &&
                                conflict.createdBy === user.id;

  if (needsToAcceptTerms) {
    return <TermsAcceptanceView conflict={conflict} user={user} onBack={onBack} onUpdate={onUpdate} />;
  }

  if (needsToReviewChanges) {
    return <ReviewProposedChangesView conflict={conflict} user={user} onBack={onBack} onUpdate={onUpdate} />;
  }

  return (
    <div className="min-h-screen p-2 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-800 mb-4 sm:mb-6 transition-colors"
        >
          ← Back to Dashboard
        </button>

        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-stone-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-700 to-orange-700 p-4 sm:p-8 text-white">
            <h1 className="text-xl sm:text-3xl font-light mb-2 break-words">{conflict.title}</h1>
            <p className="text-amber-50 mb-4 text-sm sm:text-base break-words">{conflict.problemStatement?.what || 'No description'}</p>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
              <span className="bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">
                {(conflict.status || 'pending').replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
              <span>Created {new Date(conflict.createdAt).toLocaleDateString()}</span>
              {conflict.urgency && (
                <span className={`px-3 py-1 rounded-full backdrop-blur-sm font-medium ${
                  conflict.urgency >= 4 ? 'bg-red-500/30' : conflict.urgency === 3 ? 'bg-amber-500/30' : 'bg-white/20'
                }`}>
                  Urgency: {conflict.urgency}/5
                </span>
              )}
              {conflict.needByDate && (
                <span className={`px-3 py-1 rounded-full backdrop-blur-sm font-medium ${
                  new Date(conflict.needByDate) < new Date() ? 'bg-red-500/40' : 'bg-white/20'
                }`}>
                  Due: {new Date(conflict.needByDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-stone-200 bg-stone-50">
            <div className="flex gap-1 p-2">
              {['overview', 'process', 'history'].map(tab => (
                <button
                  key={tab}
                  data-tab={tab} aria-pressed={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors capitalize text-sm sm:text-base ${
                    activeTab === tab
                      ? 'bg-white text-amber-700 shadow-sm'
                      : 'text-stone-600 hover:bg-white/50'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-8">
            {activeTab === 'overview' && (
              <ConflictOverview conflict={conflict} user={user} onUpdate={onUpdate} deactivatedUserIds={deactivatedUserIds} onNavigateToStep={(step) => {
                setForceProcessStep(step);
                setActiveTab('process');
              }} />
            )}
            {activeTab === 'process' && (
              <ConflictProcess conflict={conflict} user={user} canEdit={canEdit} onUpdate={onUpdate} onLogout={onLogout} forceStep={forceProcessStep} onStepForced={() => setForceProcessStep(null)} onReturnToOverview={async () => {
                // Re-read conflict to check for next action with fresh data
                try {
                  const fresh = await storage.get(`conflict:${conflict.id}`);
                  if (fresh) {
                    const action = getConflictAction(fresh, user);
                    if (action && action !== 'Waiting for Acceptance' && action !== 'Review & Accept Terms' && action !== 'Review Proposed Changes') {
                      // There's a process-related action — stay on process tab and refresh
                      await onUpdate();
                      return;
                    }
                  }
                } catch (e) { console.error('Error checking next action:', e); }
                setActiveTab('overview');
              }} />
            )}
            {activeTab === 'history' && (
              <ConflictHistory conflict={conflict} />
            )}
          </div>

          {/* Mentor Messaging Panel — visible to mentor and mentees */}
          {(user.role === 'mentor' || conflict.mentees?.some(m => m.id === user.id)) && (
            <div className="mt-4">
              <MentorMessagingPanel conflict={conflict} user={user} onUpdate={onUpdate} deactivatedUserIds={deactivatedUserIds} />
            </div>
          )}

          {/* Utility actions — Story Mode + Export */}
          <div className="p-4 border-t border-stone-200 bg-stone-50 rounded-b-xl sm:rounded-b-2xl flex items-center justify-center gap-3">
            {conflict.storyModeEnabled !== false && (
              <StoryModeButton conflict={conflict} user={user} compact={true} />
            )}
            <ConflictExportButton conflict={conflict} compact={true} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Conflict Overview Component
function ConflictOverview({ conflict, user, onUpdate, onNavigateToStep, deactivatedUserIds = new Set() }) {
  const toast = useToast();
  const showCrisisModal = useCrisisAlert();
  const { billingReadOnly } = useBilling();
  const isObserverRole = ['fly-on-wall', 'fly-on-the-wall', 'omniscient'].includes(user.role);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [newParticipantEmail, setNewParticipantEmail] = useState('');
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [resendingEmails, setResendingEmails] = useState(false);
  const [removingMentee, setRemovingMentee] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [avatarMap, setAvatarMap] = useState({});

  // Load avatars from tenant users list for participants who don't have one stored in the conflict
  useEffect(() => {
    const loadAvatars = async () => {
      const missingAvatars = conflict.mentees?.filter(m => !m.avatar) || [];
      if (missingAvatars.length === 0) return;
      try {
        const allTenants = await loadVisibleTenants();
        const map = {};
        for (const tenant of (Array.isArray(allTenants) ? allTenants : [])) {
          const tenantUsers = await storage.get(getTenantUsersKey(tenant.id), true) || [];
          for (const u of (Array.isArray(tenantUsers) ? tenantUsers : [])) {
            if (u.avatar && u.email) map[u.email.toLowerCase()] = u.avatar;
          }
        }
        setAvatarMap(map);
      } catch (e) {
        console.warn('[ConflictDetail] Avatar lookup failed:', e);
      }
    };
    loadAvatars();
  }, [conflict.mentees]);
  const [editData, setEditData] = useState(null);

  // Creator can edit while pending-acceptance and no other mentee has accepted
  const isResolved = conflict.status === 'resolved';
  const isCreator = conflict.createdBy === user.id;
  const isPending = conflict.status === 'pending-acceptance';
  const otherMenteeAccepted = (conflict.termsAcceptance?.acceptedBy || []).some(id => id !== conflict.createdBy);
  const canEditConflict = isCreator && isPending && !otherMenteeAccepted;

  const startEditing = () => {
    setEditData({
      title: conflict.title || '',
      urgency: conflict.urgency || 3,
      who: conflict.problemStatement?.who || '',
      what: conflict.problemStatement?.what || '',
      where: conflict.problemStatement?.where || '',
      when: conflict.problemStatement?.when || '',
      how: conflict.problemStatement?.how || '',
      desiredOutcome: conflict.desiredOutcome || '',
      needByDate: conflict.needByDate || '',
      responseHours: conflict.responseHours || '9-5',
      responseWaitTime: conflict.responseWaitTime || 15,
      responseWindow: conflict.responseWindow || 15,
      maxCommunicationCycles: conflict.maxCommunicationCycles || 5,
      menteeEmails: conflict.mentees
        ?.filter(m => m.id !== conflict.createdBy)
        .map(m => m.email) || ['']
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditData(null);
  };

  const saveEditing = async () => {
    if (!editData.title.trim()) { toast('Title is required'); return; }
    if (!editData.what.trim()) { toast('Problem description (What) is required'); return; }
    const validEmails = editData.menteeEmails.filter(e => e.trim() && e.includes('@'));
    if (validEmails.length < 1) { toast('At least one participant email is required'); return; }

    setEditSaving(true);
    try {
      // Rebuild mentees: keep creator, update invited mentees
      const creatorMentee = conflict.mentees?.find(m => m.id === conflict.createdBy);
      const newMentees = [
        creatorMentee || { id: user.id, email: user.email, name: user.name, role: 'mentee', isCreator: true },
        ...validEmails.map(email => {
          const existing = conflict.mentees?.find(m => m.email?.toLowerCase() === email.toLowerCase());
          return existing || { 
            id: `invited-${email.toLowerCase().replace(/[^a-z0-9]/g, '-')}`, 
            email: email.toLowerCase().trim(), 
            name: email.split('@')[0], 
            role: 'mentee', 
            isCreator: false 
          };
        })
      ];

      // Reset acceptedBy to only include creator if mentee list changed
      const prevEmails = conflict.mentees?.filter(m => m.id !== conflict.createdBy).map(m => m.email?.toLowerCase()).sort().join(',');
      const newEmails = validEmails.map(e => e.toLowerCase().trim()).sort().join(',');
      const menteesChanged = prevEmails !== newEmails;

      const updated = {
        ...conflict,
        title: editData.title.trim(),
        problemStatement: {
          who: editData.who.trim(),
          what: editData.what.trim(),
          where: editData.where.trim(),
          when: editData.when.trim(),
          how: editData.how.trim()
        },
        desiredOutcome: editData.desiredOutcome.trim(),
        urgency: editData.urgency,
        needByDate: editData.needByDate || null,
        responseHours: editData.responseHours,
        responseWaitTime: editData.responseWaitTime,
        responseWindow: editData.responseWindow,
        maxCommunicationCycles: editData.maxCommunicationCycles,
        mentees: newMentees,
        ...(menteesChanged && {
          termsAcceptance: {
            ...conflict.termsAcceptance,
            acceptedBy: [conflict.createdBy],
            status: 'pending'
          }
        }),
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      // Content checks on edited fields
      const editFields = [
        { text: editData.title, label: 'title' },
        { text: editData.what, label: '"What happened"' },
        { text: editData.who, label: '"Who is involved"' },
        { text: editData.how, label: '"How did it happen"' },
        { text: editData.desiredOutcome, label: '"Desired outcome"' },
      ];
      for (const field of editFields) {
        if (!field.text) continue;
        if (containsProfanity(field.text)) {
          toast(`Please remove profanity from the ${field.label}`);
          setEditSaving(false);
          return;
        }
        const toneCheck = detectUnconstructiveContent(field.text);
        if (toneCheck.isUnconstructive) {
          toast(`Please revise the ${field.label}: ${toneCheck.reason}`);
          setEditSaving(false);
          return;
        }
      }

      addAuditEntry(updated, user.id, user.name, 'Edited conflict details', null, user.email);
      await storage.set(`conflict:${conflict.id}`, updated, true);
      // Harm detection on edited text fields
      const allEditText = [editData.title, editData.what, editData.who, editData.how, editData.desiredOutcome].filter(Boolean).join(' ');
      const harmResult = detectHarmContent(allEditText);
      if (harmResult.detected) {
        if (typeof showCrisisModal === 'function') showCrisisModal(harmResult.type);
        notifyMentorOfHarmDetection(conflict, user, harmResult.type, 'conflict overview edit');
      }
      setIsEditing(false);
      setEditData(null);
      await onUpdate();
      toast('Conflict updated successfully');
    } catch (error) {
      console.error('Error saving conflict edits:', error);
      toast('Failed to save changes. Please try again.');
    }
    setEditSaving(false);
  };

  // Check if user can remove a specific mentee
  // - Mentor can remove any mentee at any stage
  // - Creator/Admin can only remove mentees who have NOT yet accepted
  const canRemoveMentee = (mentee) => {
    // No removals once conflict is resolved
    if (isResolved) return false;
    const isCreator = conflict.createdBy === user.id;
    const isMentor = user.role === 'mentor';
    const isAdmin = user.role === 'admin';
    const isMenteeCreator = mentee.id === conflict.createdBy;

    // Can never remove the conflict creator
    if (isMenteeCreator) return false;

    // Mentor can always remove any mentee
    if (isMentor) return true;
    
    // Check if this specific mentee has accepted
    const menteeHasAccepted = conflict.termsAcceptance?.acceptedBy?.includes(mentee.id);
    
    // Creator and Admin can only remove if mentee hasn't accepted yet
    if ((isCreator || isAdmin) && !menteeHasAccepted) {
      return true;
    }
    
    return false;
  };

  // Handle removing a mentee from the conflict
  const handleRemoveMentee = async (menteeToRemove) => {
    const isMenteeCreator = menteeToRemove.id === conflict.createdBy;
    
    if (isMenteeCreator) {
      toast("Cannot remove the conflict creator.");
      return;
    }

    // Check if this mentee has already accepted/responded
    const hasAccepted = conflict.termsAcceptance?.acceptedBy?.includes(menteeToRemove.id);
    const isMentor = user.role === 'mentor';
    
    // Only mentor can remove after acceptance
    if (hasAccepted && !isMentor) {
      toast("Only the mentor can remove a participant who has already accepted the terms.");
      return;
    }

    const confirmMsg = hasAccepted
      ? `"${menteeToRemove.name || menteeToRemove.email}" has already accepted the terms. Are you sure you want to remove them?`
      : `Are you sure you want to remove "${menteeToRemove.name || menteeToRemove.email}" from this conflict?`;

    if (!confirm(confirmMsg)) {
      return;
    }

    setRemovingMentee(menteeToRemove.id);
    try {
      // Re-read conflict from Firebase to avoid overwriting other users' changes
      const freshConflict = await storage.get(`conflict:${conflict.id}`, true) || conflict;

      // Remove mentee from the list (match by ID or email in case IDs diverged)
      const updatedMentees = freshConflict.mentees.filter(m =>
        m.id !== menteeToRemove.id && m.email?.toLowerCase() !== menteeToRemove.email?.toLowerCase()
      );

      // Also remove from termsAcceptance.acceptedBy if present
      const updatedAcceptedBy = freshConflict.termsAcceptance?.acceptedBy?.filter(
        id => id !== menteeToRemove.id
      ) || [];

      const updatedConflict = {
        ...freshConflict,
        mentees: updatedMentees,
        termsAcceptance: {
          ...freshConflict.termsAcceptance,
          acceptedBy: updatedAcceptedBy
        },
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      // Check if there are any remaining invited attendees (non-creator mentees)
      const remainingInvited = updatedMentees.filter(m => m.id !== conflict.createdBy);
      
      if (remainingInvited.length === 0) {
        // No invited attendees left — revert to pending-acceptance
        updatedConflict.status = 'pending-acceptance';
        updatedConflict.termsAcceptance.status = 'pending';
        updatedConflict.statusHistory = [
          ...(updatedConflict.statusHistory || []),
          { status: 'pending-acceptance', timestamp: new Date().toISOString(), reason: 'All invited attendees removed' }
        ];
      } else {
        // Re-evaluate: if all remaining mentees have now accepted, advance the status
        const allRemainingIds = remainingInvited.map(m => m.id).filter(id => id);
        const allRemainingAccepted = allRemainingIds.length > 0 && allRemainingIds.every(id => 
          updatedAcceptedBy.includes(id)
        );
        
        if (allRemainingAccepted && updatedConflict.status === 'pending-acceptance') {
          updatedConflict.termsAcceptance.status = 'accepted';
          updatedConflict.status = 'identify-define';
          updatedConflict.statusHistory = [
            ...(updatedConflict.statusHistory || []),
            { status: 'identify-define', timestamp: new Date().toISOString(), reason: 'All remaining attendees have accepted' }
          ];
        }
      }

      
      const saveResult = await storage.set(`conflict:${conflict.id}`, updatedConflict, true);
      
      // Verify the save by reading it back
      const verification = await storage.get(`conflict:${conflict.id}`, true);
      
      await onUpdate();
      
      toast(`"${menteeToRemove.name || menteeToRemove.email}" has been removed from the conflict.`);
    } catch (error) {
      console.error('Error removing mentee:', error);
      toast('Failed to remove participant. Please try again.');
    } finally {
      setRemovingMentee(null);
    }
  };

  // Check if resend is available (30 minutes after last send)
  const canResendEmails = () => {
    if (conflict.createdBy !== user.id) return false; // Only creator can resend
    
    const now = new Date();
    const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);
    
    // Find mentees who haven't responded and whose last email was sent > 30 min ago
    const menteesNeedingResend = conflict.mentees?.filter(mentee => {
      // Skip if this is the creator
      if (mentee.id === conflict.createdBy) return false;
      
      // Check if they've responded (accepted terms or participated)
      const hasResponded = conflict.termsAcceptance?.acceptedBy?.includes(mentee.id) ||
                          conflict.steps?.identifyDefine?.data?.[mentee.id];
      
      if (hasResponded) return false;
      
      // Check if 30 minutes have passed since last email
      if (!mentee.lastEmailSent) return true; // Never sent, can send now
      
      const lastSent = new Date(mentee.lastEmailSent);
      return lastSent < thirtyMinutesAgo;
    }) || [];
    
    return menteesNeedingResend.length > 0;
  };

  const handleResendEmails = async () => {
    setResendingEmails(true);
    
    try {
      const now = new Date();
      const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);
      
      // Find mentees who need resend
      const menteesToResend = conflict.mentees?.filter(mentee => {
        if (mentee.id === conflict.createdBy) return false;
        
        const hasResponded = conflict.termsAcceptance?.acceptedBy?.includes(mentee.id) ||
                            conflict.steps?.identifyDefine?.data?.[mentee.id];
        
        if (hasResponded) return false;
        
        if (!mentee.lastEmailSent) return true;
        
        const lastSent = new Date(mentee.lastEmailSent);
        return lastSent < thirtyMinutesAgo;
      }) || [];

      if (menteesToResend.length === 0) {
        toast('No participants need email resend at this time.');
        setResendingEmails(false);
        return;
      }

      // Send emails
      const emailPromises = menteesToResend.map(async (mentee) => {
        try {
          const result = await emailService.sendInvitation({
            toEmail: mentee.email,
            inviterName: user.name,
            inviterEmail: user.email,
            conflictTitle: conflict.title,
            conflictId: conflict.id,
            conflictDescription: conflict.problemStatement?.what
          });
          return { email: mentee.email, success: result.success };
        } catch (error) {
          console.error(`Failed to resend email to ${mentee.email}:`, error);
          return { email: mentee.email, success: false };
        }
      });

      const results = await Promise.allSettled(emailPromises);
      
      // Update mentee records with new send time
      const updatedMentees = conflict.mentees.map(mentee => {
        const result = results.find(r => 
          r.status === 'fulfilled' && r.value.email.toLowerCase() === mentee.email.toLowerCase()
        );
        if (result && result.value.success) {
          return {
            ...mentee,
            lastEmailSent: new Date().toISOString(),
            emailSentCount: (mentee.emailSentCount || 1) + 1
          };
        }
        return mentee;
      });

      // Save updated conflict
      const updatedConflict = { ...conflict, mentees: updatedMentees };
      await storage.set(`conflict:${conflict.id}`, updatedConflict, true);
      
      const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      toast(`Resent ${successCount} of ${menteesToResend.length} invitation emails.`);
      
      await onUpdate();
    } catch (error) {
      console.error('Error resending emails:', error);
      toast('Failed to resend emails. Please try again.');
    }
    
    setResendingEmails(false);
  };

  const getNextAction = () => {
    // Handle conflicts created before terms acceptance was added
    if (!conflict.termsAcceptance || conflict.termsAcceptance.status === 'accepted' || conflict.status !== 'pending-acceptance') {
      // Skip terms acceptance checks for older conflicts
    } else {
      // Check if user needs to accept terms — only mentees, not observers
      const isMenteeForTerms = conflict.mentees?.some(m => m.id === user.id || m.email?.toLowerCase() === user.email?.toLowerCase());
      const needsToAcceptTerms = isMenteeForTerms &&
                                 conflict.termsAcceptance?.required && 
                                 conflict.termsAcceptance?.status === 'pending' &&
                                 !conflict.termsAcceptance?.acceptedBy?.includes(user.id) &&
                                 conflict.createdBy !== user.id;
      
      if (needsToAcceptTerms) {
        return {
          text: 'Review & Accept Terms',
          description: 'You need to accept the conflict resolution terms before proceeding',
          color: 'yellow',
          action: 'terms'
        };
      }

      // Check if there are proposed changes to review
      const needsToReviewChanges = conflict.termsAcceptance?.status === 'changes-proposed' &&
                                    conflict.createdBy === user.id;
      
      if (needsToReviewChanges) {
        return {
          text: 'Review Proposed Changes',
          description: `${conflict.termsAcceptance?.proposedChanges?.proposedByName} has proposed different terms`,
          color: 'orange',
          action: 'review-changes'
        };
      }

      // Non-proposer mentee sees waiting when changes are proposed for the creator to review
      if (conflict.termsAcceptance?.status === 'changes-proposed' && conflict.createdBy !== user.id) {
        return {
          text: 'Waiting for Review',
          description: 'Proposed changes are being reviewed by the conflict creator',
          color: 'blue',
          action: 'waiting'
        };
      }

      // Check if waiting for others to accept
      if (conflict.termsAcceptance?.status === 'pending' && conflict.termsAcceptance?.acceptedBy?.includes(user.id)) {
        // Defensive: verify there actually ARE non-creator mentees who haven't accepted
        const nonCreatorMenteeIds = conflict.mentees?.filter(m => m.id !== conflict.createdBy).map(m => m.id).filter(id => id) || [];
        const acceptedBy = conflict.termsAcceptance?.acceptedBy || [];
        const allNonCreatorAccepted = nonCreatorMenteeIds.length === 0 || nonCreatorMenteeIds.every(id => acceptedBy.includes(id));
        if (!allNonCreatorAccepted) {
          return {
            text: 'Waiting for Others',
            description: 'Waiting for all parties to accept the terms',
            color: 'blue',
            action: 'waiting'
          };
        }
        // All have actually accepted but status wasn't transitioned — skip to next phase logic
      }

      // Creator waiting for mentees to accept terms
      if (conflict.termsAcceptance?.status === 'pending' && conflict.createdBy === user.id) {
        const acceptedCount = conflict.termsAcceptance?.acceptedBy?.length || 0;
        const totalMentees = conflict.mentees?.filter(m => m.id !== conflict.createdBy).length || 0;
        return {
          text: 'Waiting for Acceptance',
          description: `${acceptedCount} of ${totalMentees} participant${totalMentees !== 1 ? 's have' : ' has'} accepted the terms`,
          color: 'blue',
          action: 'waiting'
        };
      }
    }

    // Check current step and what's needed
    const isMentee = conflict.mentees?.some(m => m.id === user.id || m.email.toLowerCase() === user.email.toLowerCase());
    const isCreator = conflict.createdBy === user.id;
    const canParticipate = isMentee || isCreator;
    
    if (conflict.status === 'identify-define' || conflict.status === 'created') {
      const userResponse = conflict.steps?.identifyDefine?.data?.[user.id];
      if (!userResponse && canParticipate) {
        return {
          text: 'Submit Your Understanding',
          description: 'Paraphrase the problem statement to show your understanding',
          color: 'purple',
          action: 'process'
        };
      }
      
      const identifyDefineData = conflict.steps?.identifyDefine?.data || {};
      const menteeIds = conflict.mentees?.map(m => m.id) || [];
      // Only count responses from current participants (ignore removed participants' data)
      const activeResponseIds = Object.keys(identifyDefineData).filter(k => menteeIds.includes(k));
      const responseCount = activeResponseIds.length;
      const totalMentees = menteeIds.length || 1;
      const allResponded = responseCount >= totalMentees;

      // Check acknowledgment status
      if (allResponded) {
        let userMissingAcks = 0;
        let totalMissing = 0;
        let totalNeeded = 0;

        for (const responderId of activeResponseIds) {
          const response = identifyDefineData[responderId];
          const acks = (response.acknowledgments || []).map(a => a.userId);

          for (const menteeId of menteeIds) {
            if (menteeId !== responderId) {
              totalNeeded++;
              if (!acks.includes(menteeId)) {
                totalMissing++;
                if (menteeId === user.id) {
                  userMissingAcks++;
                }
              }
            }
          }
        }
        
        const allAcknowledged = totalMissing === 0;
        
        // User still needs to acknowledge others' paraphrases
        if (userMissingAcks > 0 && canParticipate) {
          return {
            text: 'Acknowledge Paraphrases',
            description: `You need to acknowledge ${userMissingAcks} other participant${userMissingAcks !== 1 ? 's\'' : '\'s'} paraphrase${userMissingAcks !== 1 ? 's' : ''}`,
            color: 'purple',
            action: 'process'
          };
        }
        
        // User has acknowledged all, but others haven't finished acknowledging
        if (!allAcknowledged) {
          return {
            text: 'Waiting for Acknowledgments',
            description: `${totalNeeded - totalMissing} of ${totalNeeded} acknowledgments complete`,
            color: 'blue',
            action: 'waiting'
          };
        }
        
        // All acknowledged — now check skip communication vote
        if (!conflict.steps?.identifyDefine?.skipCommunicationVotes && canParticipate) {
          return {
            text: 'Skip Communication Vote',
            description: 'Vote on whether to skip communication and go to Explore Alternatives',
            color: 'purple',
            action: 'process'
          };
        }
        
        // Check if votes are in progress
        if (conflict.steps?.identifyDefine?.skipCommunicationVotes) {
          const votes = conflict.steps.identifyDefine.skipCommunicationVotes;
          const myVote = votes[user.id];
          const allVoted = menteeIds.every(id => votes[id] !== undefined) || 
                           Object.keys(votes).length >= menteeIds.length;
          
          if (!myVote && canParticipate) {
            return {
              text: 'Vote Required',
              description: 'Vote on whether to skip communication or continue',
              color: 'purple',
              action: 'process'
            };
          }
          
          if (!allVoted) {
            return {
              text: 'Waiting for Votes',
              description: 'Waiting for all participants to vote on skipping communication',
              color: 'blue',
              action: 'waiting'
            };
          }
        }
      }
      
      if (userResponse && !allResponded) {
        return {
          text: 'Waiting for Responses',
          description: `${responseCount} of ${totalMentees} participants have responded`,
          color: 'blue',
          action: 'waiting'
        };
      }
    }

    if (conflict.status === 'communicate') {
      const cycles = conflict.steps?.communicate?.cycles || [];
      
      // NEW: No communication entries yet - show phase transition banner
      if (cycles.length === 0) {
        // Check if the current user is the one who rejected (moved it to communicate)
        // Find who voted "no" (continue to communication) in the skip communication vote
        const skipVotes = conflict.steps?.identifyDefine?.skipCommunicationVotes || {};
        const noVoter = Object.values(skipVotes).find(v => !v.skip);
        const rejectorId = noVoter?.userId || conflict.updatedBy;
        const isRejector = rejectorId === user.id;
        
        // Check if someone is actively composing
        const activeComposer = conflict.steps?.communicate?.activeComposer || null;
        const activeComposerTimestamp = conflict.steps?.communicate?.activeComposerTimestamp || null;
        const composerTimedOut = activeComposerTimestamp && ((new Date() - new Date(activeComposerTimestamp)) / (1000 * 60) > 5);
        const isActiveComposer = activeComposer === user.id;
        const someoneElseComposing = activeComposer && activeComposer !== user.id && !composerTimedOut;
        
        // Check if response window has expired since moving to communicate phase
        const communicateEntry = conflict.statusHistory?.filter(e => e.status === 'communicate').pop();
        const communicateStartTime = communicateEntry ? new Date(communicateEntry.timestamp) : null;
        const now = new Date();
        const minutesSinceStart = communicateStartTime ? (now - communicateStartTime) / (1000 * 60) : 0;
        const responseWindowExpired = minutesSinceStart > (conflict.responseWindow || 15);
        
        // Someone else is actively composing — show waiting
        if (someoneElseComposing) {
          const composerName = conflict.mentees?.find(m => m.id === activeComposer)?.name || 'Another participant';
          return {
            text: 'Participant Is Composing',
            description: `${composerName} is writing their "Because" statement. Please wait for them to finish.`,
            color: 'blue',
            action: 'waiting-communicate'
          };
        }
        
        // Active composer (this user) — prompt to continue
        if (isActiveComposer) {
          return {
            text: 'Continue Your Statement',
            description: 'You are composing the first "Because" statement. Complete and submit your perspective.',
            color: 'orange',
            action: 'communicate'
          };
        }
        
        if (isRejector && !responseWindowExpired) {
          // The rejector should be prompted to go to communicate tab
          return {
            text: 'Start Communication',
            description: 'You moved this conflict to the communication phase. Share your perspective using a "Because" statement.',
            color: 'orange',
            action: 'communicate'
          };
        } else if (responseWindowExpired) {
          // Response window expired, all mentees should see action button
          return {
            text: 'Communication Entry Overdue',
            description: 'The response window has passed with no communication entry. Take action to enter a "Because" statement.',
            color: 'red',
            action: 'communicate'
          };
        } else {
          // Other mentees: show waiting indicator with communication phase banner
          return {
            text: 'Awaiting First Communication',
            description: 'The conflict has moved to the communication phase. Waiting for the first participant to share their perspective.',
            color: 'blue',
            action: 'waiting-communicate'
          };
        }
      }
      
      const lastCycle = cycles[cycles.length - 1];
      
      // User is waiting for other person to paraphrase their statement
      if (lastCycle && !lastCycle.paraphrase && lastCycle.userId === user.id) {
        return {
          text: 'Awaiting Paraphrase',
          description: 'Waiting for the other participant to paraphrase your statement',
          color: 'blue',
          action: 'waiting'
        };
      }
      
      // User needs to paraphrase the other person's statement
      if (lastCycle && !lastCycle.paraphrase && lastCycle.userId !== user.id && canParticipate) {
        return {
          text: 'Paraphrase Response',
          description: 'Show understanding by paraphrasing the last statement',
          color: 'orange',
          action: 'process'
        };
      }
      
      // User can add their own statement — but only if it's their turn
      // 5 full cycles × 2 entries each = 10 max entries
      const maxCommunicateEntries = 10;

      // Check for satisfaction vote in progress
      const satVotes = conflict.steps?.communicate?.satisfactionVotes || {};
      const mySatVote = satVotes[user.id];
      const commMenteeIds = conflict.mentees?.map(m => m.id) || [];
      const allSatVoted = commMenteeIds.every(id => satVotes[id] !== undefined);
      const allSat = allSatVoted && Object.values(satVotes).every(v => v.satisfied);

      // Check if the satisfaction vote UI is active in CommunicateStep
      // (communicationIsComplete && not cleared by a "continue" vote)
      const commLastVoteResult = conflict.steps?.communicate?.lastVoteResult;
      const commVotesWereCleared = commLastVoteResult === 'continue' && Object.keys(satVotes).length === 0;
      const commMenteesWithEntry = new Set(cycles.filter(c => c.paraphrase).map(c => c.userId));
      const commIsComplete = cycles.length > 0 && cycles[cycles.length - 1]?.paraphrase
          && commMenteeIds.length > 0 && commMenteeIds.every(id => commMenteesWithEntry.has(id));
      const showingSatisfactionVote = commIsComplete && cycles.length < maxCommunicateEntries && !commVotesWereCleared;

      if (showingSatisfactionVote && canParticipate) {
        if (!mySatVote) {
          return {
            text: 'Vote: Continue or Move On?',
            description: Object.keys(satVotes).length > 0
              ? 'The other participant has voted. Cast your vote to continue communicating or move to Explore Alternatives.'
              : 'A communication cycle is complete. Vote to continue communicating or move to Explore Alternatives.',
            color: 'purple',
            action: 'process'
          };
        } else if (!allSatVoted) {
          return {
            text: 'Waiting for Vote',
            description: 'You\'ve voted on whether to move on. Waiting for the other participant.',
            color: 'purple',
            action: 'waiting'
          };
        }
      }

      if ((!lastCycle || lastCycle.paraphrase) && cycles.length < maxCommunicateEntries && canParticipate) {
        // Determine whose turn it is
        const lastParaphraserId = lastCycle?.paraphrasedBy || null;
        const isMyTurn = cycles.length === 0 || lastParaphraserId === user.id;
        
        if (isMyTurn) {
          return {
            text: 'Share Your Perspective',
            description: cycles.length === 0 
              ? 'Add a "Because" statement to start the communication'
              : 'You just paraphrased. It\'s your turn to share a "Because" statement',
            color: 'orange',
            action: 'process'
          };
        } else {
          return {
            text: 'Waiting for Other Participant',
            description: 'The other participant is composing their "Because" statement after paraphrasing yours',
            color: 'blue',
            action: 'waiting'
          };
        }
      }
      
      if (cycles.length >= maxCommunicateEntries) {
        return {
          text: 'Communication Complete',
          description: 'Ready to move to exploring alternatives',
          color: 'green',
          action: 'waiting'
        };
      }
    }

    if (conflict.status === 'explore-alternatives') {
      const alternatives = conflict.steps?.exploreAlternatives?.alternatives || [];
      const userAlternatives = alternatives.filter(a => a.createdBy === user.id);
      const doneUsers = conflict.steps?.exploreAlternatives?.doneUsers || [];
      const userIsDone = doneUsers.includes(user.id);
      
      if (userIsDone) {
        return {
          text: 'Waiting for Other Participant',
          description: 'You\'re done proposing. Waiting for the other participant to finish.',
          color: 'blue',
          action: 'waiting'
        };
      }
      
      if (userAlternatives.length === 0 && canParticipate) {
        return {
          text: 'Propose Solutions',
          description: 'You must propose at least 1 alternative (up to 3)',
          color: 'amber',
          action: 'process'
        };
      }

      if (userAlternatives.length >= 1 && userAlternatives.length < 3 && canParticipate) {
        return {
          text: 'Propose More or Finish',
          description: `You've proposed ${userAlternatives.length}. Add more or indicate you're done.`,
          color: 'amber',
          action: 'process'
        };
      }
      
      if (userAlternatives.length >= 3) {
        return {
          text: 'Proposals Submitted',
          description: 'You have submitted all your alternatives',
          color: 'blue',
          action: 'waiting'
        };
      }
    }

    if (conflict.status === 'evaluate-select') {
      const hasRated = conflict.steps?.evaluateSelect?.ratings?.[user.id];
      
      if (!hasRated && canParticipate) {
        return {
          text: 'Rate Solutions',
          description: 'Rate all proposed solutions to select the best one',
          color: 'yellow',
          action: 'process'
        };
      }
      
      return {
        text: 'Ratings Submitted',
        description: 'Waiting for all participants to submit their ratings',
        color: 'blue',
        action: 'waiting'
      };
    }

    if (conflict.status === 'agree-implement') {
      const hasAcknowledged = conflict.steps?.agreeImplement?.acknowledged?.find(a => a.userId === user.id);
      
      if (!hasAcknowledged && canParticipate) {
        return {
          text: 'Acknowledge Resolution',
          description: 'Review and acknowledge the selected resolution',
          color: 'green',
          action: 'process'
        };
      }
      
      return {
        text: 'Acknowledgment Submitted',
        description: 'Waiting for all participants to acknowledge the resolution',
        color: 'blue',
        action: 'waiting'
      };
    }

    if (conflict.status === 'follow-up') {
      const hasRated = conflict.steps?.followUp?.ratings?.[user.id];
      
      if (!hasRated && canParticipate) {
        return {
          text: 'Complete Follow-up',
          description: 'Rate your experience with the conflict resolution process',
          color: 'cyan',
          action: 'process'
        };
      }
      
      if (hasRated && !conflict.steps?.followUp?.completed) {
        return {
          text: 'Follow-up Submitted',
          description: 'Waiting for all participants to complete the follow-up',
          color: 'blue',
          action: 'waiting'
        };
      }
      
      return {
        text: 'View Summary',
        description: 'All ratings submitted. View the complete resolution summary',
        color: 'green',
        action: 'process'
      };
    }

    if (conflict.status === 'resolved') {
      return {
        text: 'Conflict Resolved',
        description: 'This conflict has been successfully resolved',
        color: 'green',
        action: 'none'
      };
    }

    // Default - view the process
    if (canParticipate) {
      return {
        text: 'View Process',
        description: 'Check the process tab to see the current status',
        color: 'blue',
        action: 'process'
      };
    }

    return null;
  };

  const handleAddParticipant = async () => {
    if (isObserverRole || billingReadOnly.readOnly || conflict.status === 'resolved') {
      toast('You cannot add participants to this conflict.');
      return;
    }
    if (!newParticipantEmail.trim() || !newParticipantEmail.includes('@')) {
      toast('Please enter a valid email address');
      return;
    }

    // Check if already a participant
    const alreadyExists = conflict.mentees?.some(m => 
      m.email?.toLowerCase() === newParticipantEmail.toLowerCase()
    );
    
    if (alreadyExists) {
      toast('This person is already a participant');
      return;
    }

    setAddingParticipant(true);
    try {
      // Exact-email lookup for the real ID and avatar (server returns one match, never a listing)
      const email = newParticipantEmail.trim().toLowerCase();
      let existingUser = null;
      try {
        const found = await directoryClient.find(email);
        if (found) existingUser = { ...found, email };
      } catch (e) {
        console.warn('[ConflictDetail] User lookup for add participant failed:', e);
      }

      const newMentee = existingUser ? {
        id: existingUser.id,
        email: existingUser.email,
        name: existingUser.name || existingUser.email.split('@')[0],
        avatar: existingUser.avatar,
        role: 'mentee',
        invited: true,
        addedAt: new Date().toISOString()
      } : {
        id: `mentee-${Date.now()}`,
        email: newParticipantEmail.trim(),
        name: newParticipantEmail.split('@')[0],
        role: 'mentee',
        invited: true,
        addedAt: new Date().toISOString()
      };

      // Re-read conflict from Firebase to avoid overwriting other users' changes
      const freshConflict = await storage.get(`conflict:${conflict.id}`, true) || conflict;
      const updated = {
        ...freshConflict,
        mentees: [...(freshConflict.mentees || []), newMentee],
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      // Stamp relational access snapshot before saving
      try {
        const allRels = await loadAllRelationships();
        const accessSnapshot = computeRelationalAccessSnapshot(updated, allRels);
        if (accessSnapshot.length > 0) {
          const accessDetails = accessSnapshot.map(a => `${a.name} (${a.email || 'no email'}): ${a.accessPath}`).join('; ');
          addAuditEntry(updated, user.id, user.name, 'Relational access snapshot', `Participant added: ${newMentee.name || newMentee.email}. Users with relational access: ${accessDetails}`, user.email);
        } else {
          addAuditEntry(updated, user.id, user.name, 'Relational access snapshot', `Participant added: ${newMentee.name || newMentee.email}. No users with relational access at this time.`, user.email);
        }
      } catch (snapErr) {
        console.warn('[ConflictDetail] Relational access snapshot failed:', snapErr);
      }

      await storage.set(`conflict:${conflict.id}`, updated, true);

      // Send invitation email
      try {
        const emailResult = await emailService.sendInvitation({
          toEmail: newParticipantEmail.trim(),
          inviterName: user.name,
          inviterEmail: user.email,
          conflictTitle: conflict.title,
          conflictId: conflict.id,
          conflictDescription: conflict.problemStatement?.what
        });
      } catch (emailError) {
        console.error('Failed to send invitation email:', emailError);
        // Don't block on email failure
      }
      
      // Generate invite link for manual sharing
      const inviteLink = `${window.location.origin}?invite=${conflict.id}&email=${encodeURIComponent(newParticipantEmail.trim())}`;
      
      setNewParticipantEmail('');
      setShowAddParticipant(false);
      await onUpdate();
      
      if (EMAIL_CONFIG.enabled) {
        toast('Participant added and invitation email sent!');
      } else {
        // Show the invite link for manual sharing
        toast(`Participant added!\n\nShare this link with them:\n${inviteLink}`);
      }
    } catch (error) {
      console.error('Error adding participant:', error);
      toast('Failed to add participant. Please try again.');
    }
    setAddingParticipant(false);
  };

  const nextAction = getNextAction();

  return (
    <div className="space-y-8">
      {/* Next Action CTA */}
      {nextAction && (
        <div className={`p-4 sm:p-6 rounded-xl border-2 ${
          nextAction.color === 'yellow' ? 'bg-yellow-50 border-yellow-300' :
          nextAction.color === 'orange' ? 'bg-orange-50 border-orange-300' :
          nextAction.color === 'blue' ? 'bg-blue-50 border-blue-300' :
          nextAction.color === 'purple' ? 'bg-purple-50 border-purple-300' :
          nextAction.color === 'amber' ? 'bg-amber-50 border-amber-300' :
          nextAction.color === 'green' ? 'bg-green-50 border-green-300' :
          nextAction.color === 'red' ? 'bg-red-50 border-red-300' :
          'bg-stone-50 border-stone-300'
        }`}>
          {/* Communication Phase Visual Indicator */}
          {nextAction.action === 'waiting-communicate' && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-blue-100 rounded-lg border border-blue-200">
              <div className="relative flex-shrink-0">
                <MessageSquare className="w-6 h-6 text-blue-600" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
              </div>
              <div>
                <p className="text-sm font-medium text-blue-800">Communication Phase Started</p>
                <p className="text-xs text-blue-700">Participants voted to continue with communication. Perspectives will be shared through structured "Because" statements.</p>
              </div>
            </div>
          )}
          {nextAction.action === 'communicate' && conflict.status === 'communicate' && (conflict.steps?.communicate?.cycles || []).length === 0 && nextAction.color === 'red' && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-red-100 rounded-lg border border-red-200">
              <div className="relative flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-red-800">Response Window Expired</p>
                <p className="text-xs text-red-700">No communication entry has been submitted within the agreed response time. All participants may now submit a "Because" statement.</p>
              </div>
            </div>
          )}
          <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1">
              <div className={`text-lg font-medium mb-1 ${
                nextAction.color === 'yellow' ? 'text-yellow-800' :
                nextAction.color === 'orange' ? 'text-orange-800' :
                nextAction.color === 'blue' ? 'text-blue-800' :
                nextAction.color === 'purple' ? 'text-purple-800' :
                nextAction.color === 'amber' ? 'text-amber-800' :
                nextAction.color === 'green' ? 'text-green-800' :
                nextAction.color === 'red' ? 'text-red-800' :
                'text-stone-800'
              }`}>
                {nextAction.text}
              </div>
              <p className={`text-sm ${
                nextAction.color === 'yellow' ? 'text-yellow-700' :
                nextAction.color === 'orange' ? 'text-orange-700' :
                nextAction.color === 'blue' ? 'text-blue-700' :
                nextAction.color === 'purple' ? 'text-purple-700' :
                nextAction.color === 'amber' ? 'text-amber-700' :
                nextAction.color === 'green' ? 'text-green-700' :
                nextAction.color === 'red' ? 'text-red-700' :
                'text-stone-700'
              }`}>
                {nextAction.description}
              </p>
            </div>
            {nextAction.action !== 'waiting' && nextAction.action !== 'waiting-communicate' && nextAction.action !== 'none' && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (nextAction.action === 'communicate' && onNavigateToStep) {
                    // Navigate directly to the communicate step via callback
                    onNavigateToStep('communicate');
                  } else {
                    // Switch to process tab
                    const processTab = document.querySelector('[data-tab="process"]');
                    if (processTab) processTab.click();
                  }
                }}
                className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98] whitespace-nowrap text-center ${
                  nextAction.color === 'yellow' ? 'bg-yellow-600 hover:bg-yellow-500 text-stone-900' :
                  nextAction.color === 'orange' ? 'bg-orange-700 hover:bg-orange-800 text-white' :
                  nextAction.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                  nextAction.color === 'purple' ? 'bg-purple-600 hover:bg-purple-700 text-white' :
                  nextAction.color === 'amber' ? 'bg-amber-700 hover:bg-amber-800 text-white' :
                  nextAction.color === 'green' ? 'bg-green-700 hover:bg-green-800 text-white' :
                  nextAction.color === 'red' ? 'bg-red-600 hover:bg-red-700 text-white' :
                  'bg-stone-600 hover:bg-stone-700 text-white'
                }`}
              >
                Take Action →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Edit Conflict Button */}
      {canEditConflict && !isEditing && (
        <div className="flex justify-end">
          <button
            onClick={startEditing}
            className="flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-sm font-medium transition-colors"
          >
            <Settings className="w-4 h-4" />
            Edit Conflict
          </button>
        </div>
      )}

      {/* Editing Banner */}
      {isEditing && (
        <div className="p-3 bg-amber-50 border-2 border-amber-300 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0" />
          <div className="flex-1">
            <span className="text-sm font-medium text-amber-800">Editing mode</span>
            <span className="text-xs text-amber-700 ml-2">Changes will be visible to invited participants</span>
          </div>
          <div className="flex gap-2">
            <button onClick={cancelEditing} className="px-3 py-1.5 text-sm text-stone-600 hover:bg-stone-100 rounded-lg transition-colors">Cancel</button>
            <button onClick={saveEditing} disabled={editSaving} className="px-4 py-1.5 text-sm bg-amber-700 hover:bg-amber-800 disabled:bg-amber-400 text-white rounded-lg font-medium transition-colors">
              {editSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Title (editable) */}
      {isEditing && (
        <div>
          <label htmlFor="edit-conflict-title" className="block text-sm font-medium text-stone-700 mb-1">Title</label>
          <input id="edit-conflict-title"
            type="text"
            value={editData.title}
            onChange={(e) => setEditData({ ...editData, title: e.target.value })}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        </div>
      )}

      {/* Urgency (editable) */}
      {isEditing && (
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Urgency Level</label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map(level => (
              <button
                key={level}
                type="button"
                onClick={() => {
                  const updates = { urgency: level };
                  if (level >= 4) updates.responseWindow = 5;
                  else if (editData.responseWindow === 5 && editData.urgency >= 4) updates.responseWindow = 15;
                  setEditData({ ...editData, ...updates });
                }}
                className={`flex-1 py-2 rounded-lg font-medium text-sm transition-all border-2 ${
                  editData.urgency === level
                    ? level >= 4 ? 'bg-red-600 border-red-600 text-white scale-105'
                      : level === 3 ? 'bg-amber-500 border-amber-500 text-stone-900 scale-105'
                      : 'bg-blue-600 border-blue-500 text-white scale-105'
                    : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300'
                }`}
              >
                {level}
              </button>
            ))}
          </div>
          {editData.urgency >= 4 && (
            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Response window set to 5 minutes
            </p>
          )}
        </div>
      )}

      {/* Problem Statement */}
      <div>
        <h3 className="text-lg sm:text-xl font-medium text-stone-800 mb-3 sm:mb-4">Problem Statement</h3>
        {isEditing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: 'who', label: 'Who is involved?' },
              { key: 'what', label: 'What happened?' },
              { key: 'where', label: 'Where did it happen?' },
              { key: 'when', label: 'When did it happen?' }
            ].map(field => (
              <div key={field.key}>
                <label htmlFor={`edit-conflict-${field.key}`} className="block text-xs font-medium text-stone-600 mb-1">{field.label}</label>
                <textarea id={`edit-conflict-${field.key}`}
                  value={editData[field.key]}
                  onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  rows={2}
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label htmlFor="edit-conflict-how" className="block text-xs font-medium text-stone-600 mb-1">How did it happen?</label>
              <textarea id="edit-conflict-how"
                value={editData.how}
                onChange={(e) => setEditData({ ...editData, how: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                rows={2}
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            <InfoCard label="Who" value={conflict.problemStatement.who} />
            <InfoCard label="What" value={conflict.problemStatement?.what || 'Not specified'} />
            <InfoCard label="Where" value={conflict.problemStatement.where} />
            <InfoCard label="When" value={conflict.problemStatement.when} />
            <InfoCard label="How" value={conflict.problemStatement.how} colSpan={2} />
          </div>
        )}
      </div>

      {/* Outcomes */}
      <div>
        <h3 className="text-lg sm:text-xl font-medium text-stone-800 mb-3 sm:mb-4">Outcomes</h3>
        {isEditing ? (
          <div>
            <label htmlFor="edit-conflict-desired-outcome" className="block text-xs font-medium text-stone-600 mb-1">Desired Outcome</label>
            <textarea id="edit-conflict-desired-outcome"
              value={editData.desiredOutcome}
              onChange={(e) => setEditData({ ...editData, desiredOutcome: e.target.value })}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              rows={2}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            <InfoCard label="Desired Outcome" value={conflict.desiredOutcome} />
            <InfoCard 
              label="Actual Outcome" 
              value={conflict.actualOutcome || 'Not yet determined'} 
              className={!conflict.actualOutcome ? 'text-stone-500 italic' : ''}
            />
          </div>
        )}
      </div>

      {/* Participants */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-lg sm:text-xl font-medium text-stone-800">Participants</h3>
          {!isResolved && !billingReadOnly?.readOnly && !isObserverRole && (
          <div className="flex flex-wrap gap-2">
            {canResendEmails() && (
              <button
                onClick={handleResendEmails}
                disabled={resendingEmails}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm rounded-lg font-medium transition-colors"
              >
                <Send className="w-4 h-4" />
                {resendingEmails ? 'Sending...' : 'Resend Invites'}
              </button>
            )}
            <button
              onClick={() => {
                const inviteLink = `${window.location.origin}?invite=${conflict.id}`;
                navigator.clipboard.writeText(inviteLink);
                toast('Invite link copied to clipboard!\n\nShare this link with participants:\n' + inviteLink);
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-stone-600 hover:bg-stone-700 text-white text-sm rounded-lg font-medium transition-colors"
            >
              <Mail className="w-4 h-4" />
              Copy Invite Link
            </button>
            <button
              onClick={() => setShowAddParticipant(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-amber-700 hover:bg-amber-800 text-white text-sm rounded-lg font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Participant
            </button>
          </div>
          )}
        </div>
        {/* Debug info */}
        <div className="text-xs text-stone-500 mb-2">
          Total mentees: {conflict.mentees?.length || 0}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {conflict.mentees?.map((mentee, idx) => {
            // Check if this mentee has responded
            const hasResponded = conflict.termsAcceptance?.acceptedBy?.includes(mentee.id) ||
                                conflict.steps?.identifyDefine?.data?.[mentee.id];
            const isCreator = mentee.id === conflict.createdBy;
            const canRemove = canRemoveMentee(mentee);
            
            return (
              <div key={idx} className="flex items-center gap-3 p-4 bg-stone-50 rounded-lg">
                <AnimalAvatar animal={mentee.avatar || avatarMap[mentee.email?.toLowerCase()]} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-stone-800 truncate flex items-center gap-1.5">
                    {mentee.email || mentee.name || 'Participant'}
                    {deactivatedUserIds.has(mentee.id) && (
                      <span className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded-full font-normal">(deactivated)</span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 flex items-center gap-2">
                    <span className="capitalize">{isCreator ? 'Creator' : 'Participant'}</span>
                    {!isCreator && (
                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                        hasResponded ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {hasResponded ? 'Responded' : 'Pending'}
                      </span>
                    )}
                  </div>
                </div>
                {canRemove && (
                  <button
                    onClick={() => handleRemoveMentee(mentee)}
                    disabled={removingMentee === mentee.id}
                    className="p-2 text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Remove participant"
                  >
                    {removingMentee === mentee.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            );
          })}
          {conflict.mentor && (
            <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-medium flex-shrink-0">
                {(conflict.mentor.email || conflict.mentor.name || 'M')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-stone-800 truncate">
                  {conflict.mentor.email || conflict.mentor.name}
                </div>
                <div className="text-xs text-stone-500">Mentor</div>
              </div>
            </div>
          )}
        </div>

        {/* Add Participant Modal */}
        {showAddParticipant && (
          <ModalDialog onClose={() => setShowAddParticipant(false)} labelledBy="add-participant-title" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddParticipant(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h4 id="add-participant-title" className="text-xl font-medium text-stone-800">Add Participant</h4>
                <button
                  onClick={() => setShowAddParticipant(false)}
                  className="p-1 hover:bg-stone-100 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-stone-500" />
                </button>
              </div>
              
              <div className="mb-4">
                <label htmlFor="add-participant-email" className="block text-sm font-medium text-stone-700 mb-2">
                  Email Address
                </label>
                <input id="add-participant-email"
                  type="email"
                  value={newParticipantEmail}
                  onChange={(e) => setNewParticipantEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddParticipant();
                    }
                  }}
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="participant@example.com"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleAddParticipant}
                  disabled={addingParticipant || !newParticipantEmail.trim()}
                  className="flex-1 bg-amber-700 hover:bg-amber-800 disabled:bg-stone-300 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-colors"
                >
                  {addingParticipant ? 'Adding...' : 'Add Participant'}
                </button>
                <button
                  onClick={() => setShowAddParticipant(false)}
                  className="px-6 py-3 bg-stone-200 hover:bg-stone-300 text-stone-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </ModalDialog>
        )}
      </div>

      {/* Settings */}
      <div>
        <h3 className="text-lg sm:text-xl font-medium text-stone-800 mb-3 sm:mb-4">Response Settings</h3>
        {isEditing ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label htmlFor="edit-response-hours" className="block text-xs font-medium text-stone-600 mb-1">Response Hours</label>
              <select id="edit-response-hours"
                value={editData.responseHours}
                onChange={(e) => setEditData({ ...editData, responseHours: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              >
                <option value="9-5">9 AM to 5 PM</option>
                <option value="5-9">5 PM to 9 PM</option>
                <option value="24">24 Hours</option>
              </select>
            </div>
            <div>
              <label htmlFor="edit-wait-time" className="block text-xs font-medium text-stone-600 mb-1">Wait Time (min)</label>
              <input id="edit-wait-time"
                type="number"
                value={editData.responseWaitTime}
                onChange={(e) => setEditData({ ...editData, responseWaitTime: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                min={1}
              />
            </div>
            <div>
              <label htmlFor="edit-response-window" className="block text-xs font-medium text-stone-600 mb-1">Response Window (min)</label>
              <input id="edit-response-window"
                type="number"
                value={editData.responseWindow}
                onChange={(e) => setEditData({ ...editData, responseWindow: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                min={1}
              />
            </div>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
            <InfoCard label="Urgency" value={
              conflict.urgency
                ? `${conflict.urgency}/5: ${['', 'Low', 'Minor', 'Moderate', 'High', 'Critical'][conflict.urgency]}`
                : 'Not set'
            } />
            <InfoCard label="Response Hours" value={conflict.responseHours} />
            <InfoCard label="Wait Time" value={`${conflict.responseWaitTime} minutes`} />
            <InfoCard label="Response Window" value={`${conflict.responseWindow} minutes`} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 mt-2">
            <InfoCard label="Need By" value={
              conflict.needByDate
                ? new Date(conflict.needByDate).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                : 'No deadline'
            } className={conflict.needByDate && new Date(conflict.needByDate) < new Date() ? 'text-red-600' : ''} />
            <InfoCard label="Max Comm. Cycles" value={`${conflict.maxCommunicationCycles || 5} cycles`} />
          </div>
          </>
        )}

        {/* Participant Emails (editable) */}
        {isEditing && (
          <div className="mt-4">
            <label className="block text-xs font-medium text-stone-600 mb-1">Invited Participants</label>
            <div className="space-y-2">
              {editData.menteeEmails.map((email, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="email"
                    aria-label={`Participant ${idx + 1} email`}
                    value={email}
                    onChange={(e) => {
                      const updated = [...editData.menteeEmails];
                      updated[idx] = e.target.value;
                      setEditData({ ...editData, menteeEmails: updated });
                    }}
                    className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                    placeholder="participant@example.com"
                  />
                  {editData.menteeEmails.length > 1 && (
                    <button
                      onClick={() => setEditData({ ...editData, menteeEmails: editData.menteeEmails.filter((_, i) => i !== idx) })}
                      className="p-2 text-stone-500 hover:text-red-500 transition-colors"
                      aria-label="Remove participant"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setEditData({ ...editData, menteeEmails: [...editData.menteeEmails, ''] })}
                className="text-sm text-amber-700 hover:text-amber-800 font-medium"
              >
                + Add another participant
              </button>
            </div>
          </div>
        )}

        {/* AI Paraphrase Assistance - toggleable by mentor/admin */}
        {!isEditing && (
        <div className="mt-4 p-4 bg-stone-50 rounded-lg border border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-stone-700">AI Paraphrase Assistance</div>
              <p className="text-xs text-stone-500 mt-1">
                {conflict.aiParaphraseAssist !== false 
                  ? 'Enabled. Participants who struggle will be offered AI-generated paraphrase suggestions as starting points.'
                  : 'Disabled. Participants must write their own paraphrases without AI assistance.'}
              </p>
            </div>
            {(user.role === 'mentor' || user.role === 'admin') && !isResolved ? (
              <button
                type="button"
                role="switch" aria-label="AI paraphrase assistance" aria-checked={conflict.aiParaphraseAssist !== false}
                onClick={async () => {
                  const updated = {
                    ...conflict,
                    aiParaphraseAssist: conflict.aiParaphraseAssist === false ? true : false,
                    updatedAt: new Date().toISOString(),
                    updatedBy: user.id
                  };
                  await storage.set(`conflict:${conflict.id}`, updated, true);
                  onUpdate();
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                  conflict.aiParaphraseAssist !== false ? 'bg-blue-600' : 'bg-stone-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  conflict.aiParaphraseAssist !== false ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            ) : (
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                conflict.aiParaphraseAssist !== false 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-stone-200 text-stone-600'
              }`}>
                {conflict.aiParaphraseAssist !== false ? 'Enabled' : 'Disabled'}
              </span>
            )}
          </div>
        </div>
        )}

        {/* Story Mode Toggle */}
        {!isEditing && (
        <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-stone-700">Story Mode</div>
              <p className="text-xs text-stone-500 mt-1">
                {conflict.storyModeEnabled !== false 
                  ? 'Enabled. Participants can view their conflict journey retold as a narrative using their animal avatars.'
                  : 'Disabled. The Story Mode button will not appear for participants.'}
              </p>
            </div>
            {(conflict.createdBy === user.id || conflict.mentees?.some(m => m.id === user.id) || user.role === 'mentor' || user.role === 'admin') && !isResolved ? (
              <button
                type="button"
                role="switch" aria-label="Story mode" aria-checked={conflict.storyModeEnabled !== false}
                onClick={async () => {
                  const updated = {
                    ...conflict,
                    storyModeEnabled: conflict.storyModeEnabled === false ? true : false,
                    updatedAt: new Date().toISOString(),
                    updatedBy: user.id
                  };
                  await storage.set(`conflict:${conflict.id}`, updated, true);
                  onUpdate();
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                  conflict.storyModeEnabled !== false ? 'bg-blue-600' : 'bg-stone-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  conflict.storyModeEnabled !== false ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            ) : (
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                conflict.storyModeEnabled !== false 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-stone-200 text-stone-600'
              }`}>
                {conflict.storyModeEnabled !== false ? 'Enabled' : 'Disabled'}
              </span>
            )}
          </div>
        </div>
        )}

        {/* Mentor Conflict Settings — per-conflict chat + resolution mentoring toggles */}
        {!isEditing && user.role === 'mentor' && !isResolved && (
          <MentorConflictSettings conflict={conflict} user={user} />
        )}
      </div>
      {isEditing && (
        <div className="flex gap-3 justify-end pt-2">
          <button onClick={cancelEditing} className="px-5 py-2.5 text-stone-600 hover:bg-stone-100 rounded-lg font-medium transition-colors">Cancel</button>
          <button onClick={saveEditing} disabled={editSaving} className="px-6 py-2.5 bg-amber-700 hover:bg-amber-800 disabled:bg-amber-400 text-white rounded-lg font-medium transition-colors">
            {editSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}

// Info Card Component

// Per-conflict mentor settings — chat toggle + resolution mentoring toggle
// Reads/writes mentor-settings:{mentorId} in shared storage
function MentorConflictSettings({ conflict, user }) {
  const toast = useToast();
  const [settings, setSettings] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    storage.get(`mentor-settings:${user.id}`, true).then(s => {
      setSettings(s || { chatEnabled: true, resolutionMentoringEnabled: false, conflictOverrides: {} });
      setLoading(false);
    }).catch(() => {
      setSettings({ chatEnabled: true, resolutionMentoringEnabled: false, conflictOverrides: {} });
      setLoading(false);
    });
  }, [user.id]);

  if (loading || !settings) return null;

  const overrides = settings.conflictOverrides || {};
  const cOverride = overrides[conflict.id] || {};
  const chatOn = cOverride.chatEnabled !== undefined ? cOverride.chatEnabled : settings.chatEnabled;
  const resOn = cOverride.resolutionMentoringEnabled !== undefined ? cOverride.resolutionMentoringEnabled : settings.resolutionMentoringEnabled;
  const hasOverride = cOverride.chatEnabled !== undefined || cOverride.resolutionMentoringEnabled !== undefined;

  const save = async (newSettings) => {
    setSettings(newSettings);
    try { await storage.set(`mentor-settings:${user.id}`, newSettings, true); } catch(e) {}
  };

  const toggleChat = () => {
    const newOverrides = { ...overrides, [conflict.id]: { ...cOverride, chatEnabled: !chatOn } };
    save({ ...settings, conflictOverrides: newOverrides });
    toast(!chatOn ? 'Chat enabled for this conflict' : 'Chat disabled for this conflict');
  };

  const toggleRes = () => {
    const newOverrides = { ...overrides, [conflict.id]: { ...cOverride, resolutionMentoringEnabled: !resOn } };
    save({ ...settings, conflictOverrides: newOverrides });
    toast(!resOn ? 'Resolution mentoring enabled for this conflict' : 'Resolution mentoring disabled for this conflict');
  };

  const resetToGlobal = () => {
    const newOverrides = { ...overrides };
    delete newOverrides[conflict.id];
    save({ ...settings, conflictOverrides: newOverrides });
    toast('Reset to global defaults');
  };

  return (
    <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-purple-800 flex items-center gap-2">
          <Settings className="w-4 h-4" /> Mentor Settings for This Conflict
        </div>
        {hasOverride && (
          <button onClick={resetToGlobal} className="text-[10px] text-purple-600 hover:text-purple-700 font-medium">Reset to global</button>
        )}
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-medium text-stone-700">Mentor Chat</div>
            <p className="text-[10px] text-stone-500">{chatOn ? 'Participants can message you' : 'Chat is hidden from participants'}</p>
          </div>
          <button type="button" onClick={toggleChat} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ml-3 ${chatOn ? 'bg-purple-600' : 'bg-stone-300'}`}>
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${chatOn ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-medium text-stone-700">Resolution Mentoring</div>
            <p className="text-[10px] text-stone-500">{resOn ? 'You review alternatives before voting' : 'Participants vote without your review'}</p>
          </div>
          <button type="button" onClick={toggleRes} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ml-3 ${resOn ? 'bg-purple-600' : 'bg-stone-300'}`}>
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${resOn ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
          </button>
        </div>
      </div>
      {hasOverride && (
        <p className="text-[10px] text-purple-600 mt-2">These settings override your global defaults for this conflict only.</p>
      )}
    </div>
  );
}

export default ConflictDetailView;
